// Creative Strategist project detail (Task #2375, spec §49/§50).
//
// The eight-tab shell. Overview, Plan, Tasks, Files and Activity are real;
// Deliverables, Decisions and Results are deliberate empty states that later
// phases fill in — never dead links.
//
// Two kinds of blocker meet on this page and both are shown. Persisted blocker
// rows (Task #2380) are the ones a human can act on right here — pick a
// template, propose a candidate. Resolution blockers (Task #2378) are computed
// facts about the plan, such as a gated tool, and exist before any row does.
//
// Tab selection uses the repo's search-param deep-link convention (?tab=...).
// The spec's path-style links (…/projects/:id/tasks) route here too and are
// folded onto the same param on mount.

import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useLocation, useParams, useSearch } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowLeft,
  CalendarClock,
  CheckCircle2,
  Compass,
  FileText,
  ListChecks,
  Package,
  Scale,
  ShieldAlert,
  Sparkles,
  UserCheck,
} from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { CreativeStrategistDeliverablesTab } from "@/components/creative-strategist/deliverables-tab";
import { CreativeStrategistResultsTab } from "@/components/creative-strategist/results-tab";
import {
  CS_PROJECT_STATUS_CLASSES,
  CS_PROJECT_STATUS_LABELS,
  CS_PROJECT_TABS,
  CS_PROJECT_TAB_LABELS,
  csBlockersKey,
  csFormatDateTime,
  csPlanKey,
  csTemplatesKey,
  type CreativeStrategistActivityEvent,
  type CreativeStrategistBlocker,
  type CreativeStrategistPlanBlocker,
  type CreativeStrategistPlanState,
  type CreativeStrategistProject,
  type CreativeStrategistProjectTab,
  type CreativeStrategistTemplateBoard,
  type CreativeStrategistTemplateEntry,
} from "@/lib/creative-strategist";
import { CreativeStrategistChatPanel } from "@/components/creative-strategist/chat-panel";
import { CreativeStrategistDecisionsTab } from "@/components/creative-strategist/decisions-tab";
import { CreativeStrategistFilesTab } from "@/components/creative-strategist/files-tab";
import { CreativeStrategistPlanTab } from "@/components/creative-strategist/plan-tab";
import { CreativeStrategistTasksTab } from "@/components/creative-strategist/tasks-tab";

function isTab(value: string | null | undefined): value is CreativeStrategistProjectTab {
  return !!value && (CS_PROJECT_TABS as readonly string[]).includes(value);
}

export default function CreativeStrategistProjectPage() {
  const { brandId, projectId, tab: pathTab } = useParams<{
    brandId: string;
    projectId: string;
    tab?: string;
  }>();
  const [, navigate] = useLocation();
  const search = useSearch();

  const basePath = `/brands/${brandId}/creative-strategist/projects/${projectId}`;
  const paramTab = new URLSearchParams(search).get("tab");
  const activeTab: CreativeStrategistProjectTab = isTab(paramTab)
    ? paramTab
    : isTab(pathTab)
      ? pathTab
      : "overview";

  // Fold a spec path-style tab link onto the ?tab= convention so every tab has
  // one canonical, linkable URL.
  useEffect(() => {
    if (pathTab) {
      navigate(isTab(pathTab) ? `${basePath}?tab=${pathTab}` : basePath, { replace: true });
    }
  }, [pathTab, basePath, navigate]);

  const { data: project, isLoading } = useQuery<CreativeStrategistProject>({
    queryKey: [`/api/creative-strategist/projects/${projectId}`],
    enabled: !!projectId,
  });

  const { data: activity, isLoading: activityLoading } = useQuery<
    CreativeStrategistActivityEvent[]
  >({
    queryKey: [`/api/creative-strategist/projects/${projectId}/activity`],
    enabled: !!projectId,
  });

  // Chat lives at project level so the conversation survives tab switches.
  // Open by default on desktop (and always when arriving from the home hero,
  // which hands over `?chat=1`); on mobile it is a drawer behind its button.
  const [chatOpen, setChatOpen] = useState(() => {
    if (typeof window === "undefined") return false;
    const wants = new URLSearchParams(window.location.search).get("chat") === "1";
    return wants || window.innerWidth >= 768;
  });
  const [focusMessageId, setFocusMessageId] = useState<string | null>(null);

  // Which task the Tasks tab has drilled into. Deep-linkable via ?taskId= so a
  // chat message about a specific task can point straight at it.
  const paramTaskId = new URLSearchParams(search).get("taskId");
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(paramTaskId);
  useEffect(() => {
    if (paramTaskId) setSelectedTaskId(paramTaskId);
  }, [paramTaskId]);

  // Which deliverable the library has open, so a chat message like "make the
  // headline punchier" lands on it without the user naming an id.
  const [viewingDeliverableId, setViewingDeliverableId] = useState<string | null>(null);

  // What the user is looking at, sent with every message so "redo this" lands
  // on the right thing.
  const viewingContext = {
    tab: activeTab,
    ...(activeTab === "tasks" && selectedTaskId ? { taskId: selectedTaskId } : {}),
    ...(activeTab === "deliverables" && viewingDeliverableId
      ? { deliverableId: viewingDeliverableId }
      : {}),
  };
  const viewingLabel = `the ${CS_PROJECT_TAB_LABELS[activeTab]} tab`;

  // Blockers are work the Strategist refused to guess its way past — they show
  // on Overview and Plan so they cannot be missed (Task #2380).
  const { data: blockers } = useQuery<CreativeStrategistBlocker[]>({
    queryKey: [csBlockersKey(projectId)],
    enabled: !!projectId,
  });
  const openBlockers = (blockers ?? []).filter((b) => b.status === "open");

  // The resolution carries its own blockers — a gated tool is a property of the
  // plan, not of a task row, so it exists before any row does.
  const { data: planState } = useQuery<CreativeStrategistPlanState>({
    queryKey: [csPlanKey(projectId)],
    enabled: !!projectId,
  });
  const planBlockers = planState?.plan?.blockers ?? [];

  if (isLoading) {
    return (
      <div className="p-8 max-w-5xl mx-auto space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="p-8 max-w-5xl mx-auto">
        <Card>
          <CardContent className="py-12 text-center space-y-3">
            <div className="text-sm text-muted-foreground">This project could not be loaded.</div>
            <Button
              variant="outline"
              onClick={() => navigate(`/brands/${brandId}/creative-strategist`)}
              data-testid="button-back-to-creative-strategist"
            >
              Back to Creative Strategist
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const stats = project.stats;
  const blockerCount =
    stats.tasksBlocked +
    stats.tasksFailed +
    (stats.blockersOpen ?? 0) +
    (stats.decisionsRejected ?? 0) +
    planBlockers.length;
  const awaitingUser =
    stats.tasksAwaitingUser + stats.deliverablesAwaitingUser + stats.decisionsAwaitingUser;

  return (
    <div className="flex min-h-screen">
      <div className="flex-1 min-w-0 p-8 max-w-5xl mx-auto space-y-6">
      <div className="space-y-3 fade-in-up">
        <button
          type="button"
          onClick={() => navigate(`/brands/${brandId}/creative-strategist`)}
          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          data-testid="link-back-creative-strategist"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Creative Strategist
        </button>

        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <h1
              className="text-2xl font-bold tracking-tight"
              data-testid="text-project-title"
            >
              {project.title}
            </h1>
            <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
              <Compass className="h-3.5 w-3.5" />
              {project.brandName ?? "Brand"} · created {csFormatDateTime(project.createdAt)}
            </div>
          </div>
          <Badge
            variant="secondary"
            className={CS_PROJECT_STATUS_CLASSES[project.status] ?? ""}
            data-testid="badge-project-status"
          >
            {CS_PROJECT_STATUS_LABELS[project.status] ?? project.status}
          </Badge>
        </div>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(value) =>
          navigate(value === "overview" ? basePath : `${basePath}?tab=${value}`, { replace: true })
        }
        className="space-y-6"
      >
        <TabsList className="flex flex-wrap h-auto justify-start" data-testid="tabs-project">
          {CS_PROJECT_TABS.map((tab) => (
            <TabsTrigger key={tab} value={tab} data-testid={`tab-${tab}`}>
              {CS_PROJECT_TAB_LABELS[tab]}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <Card>
            <CardContent className="p-6 space-y-5">
              <div>
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Objective
                </div>
                <p className="text-sm mt-1.5 whitespace-pre-wrap" data-testid="text-project-objective">
                  {project.objective || "No objective captured yet."}
                </p>
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>Progress</span>
                  <span data-testid="text-project-progress">{project.progressPercentage}%</span>
                </div>
                <Progress value={project.progressPercentage} className="h-1.5" />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <OverviewStat
                  icon={ListChecks}
                  label="Tasks complete"
                  value={`${stats.tasksComplete}/${stats.tasksTotal}`}
                  testId="stat-tasks-complete"
                />
                <OverviewStat
                  icon={Package}
                  label="Deliverables"
                  value={`${stats.deliverablesApproved}/${stats.deliverablesTotal}`}
                  testId="stat-deliverables"
                />
                <OverviewStat
                  icon={Scale}
                  label="Blockers"
                  value={blockerCount}
                  testId="stat-blockers"
                />
                <OverviewStat
                  icon={UserCheck}
                  label="Awaiting you"
                  value={awaitingUser}
                  testId="stat-awaiting-user"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
                <div>
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Current work
                  </div>
                  <p className="text-sm mt-1.5" data-testid="text-project-current-phase">
                    {project.currentPhase || "Nothing running yet."}
                  </p>
                </div>
                <div>
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Next expected step
                  </div>
                  <p className="text-sm mt-1.5" data-testid="text-project-next-step">
                    {project.status === "planning"
                      ? "Creative Strategist will turn this objective into a plan."
                      : "—"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Work the system ran out of automatic revisions on, or escalated.
              It cannot fix itself — leaving it only in the Tasks tab is how a
              project quietly stalls. (Task #2382, spec §55.) */}
          {(stats.tasksNeedingAttention ?? 0) > 0 && (
            <Card
              className="border-amber-200 dark:border-amber-900"
              data-testid="overview-needs-attention"
            >
              <CardContent className="p-4 flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-amber-700 dark:text-amber-300">
                    {stats.tasksNeedingAttention}{" "}
                    {stats.tasksNeedingAttention === 1 ? "task needs" : "tasks need"} your
                    attention
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Review couldn't get{" "}
                    {stats.tasksNeedingAttention === 1 ? "this one" : "these"} to pass on its own.
                    Take a look, then approve the work or tell it what to change.
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => navigate(`${basePath}?tab=tasks`, { replace: true })}
                  data-testid="button-open-attention-tasks"
                >
                  Review work
                </Button>
              </CardContent>
            </Card>
          )}

          {stats.decisionsRejected > 0 && (
            <Card
              className="border-red-200 dark:border-red-900"
              data-testid="overview-rejected-decisions"
            >
              <CardContent className="p-4 flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-red-700 dark:text-red-300">
                    {stats.decisionsRejected} rejected{" "}
                    {stats.decisionsRejected === 1 ? "decision" : "decisions"}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Work that depended on {stats.decisionsRejected === 1 ? "it" : "them"} is on
                    hold. Replace or reconsider the decision to let those tasks run.
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => navigate(`${basePath}?tab=decisions`, { replace: true })}
                  data-testid="button-open-decisions"
                >
                  Review decisions
                </Button>
              </CardContent>
            </Card>
          )}

          <BlockerList
            projectId={projectId}
            brandId={brandId}
            blockers={openBlockers}
            surface="overview"
          />

          {planBlockers.length > 0 && (
            <OverviewBlockers
              blockers={planBlockers}
              onOpenPlan={() => navigate(`${basePath}?tab=plan`, { replace: true })}
            />
          )}
        </TabsContent>

        <TabsContent value="plan" className="space-y-4">
          <BlockerList
            projectId={projectId}
            brandId={brandId}
            blockers={openBlockers}
            surface="plan"
          />
          <CreativeStrategistPlanTab
            projectId={projectId!}
            onApproved={() => navigate(`${basePath}?tab=tasks`, { replace: true })}
          />
        </TabsContent>

        <TabsContent value="tasks">
          <CreativeStrategistTasksTab
            projectId={projectId!}
            projectStatus={project?.status ?? null}
            selectedTaskId={selectedTaskId}
            onSelectTask={setSelectedTaskId}
          />
        </TabsContent>

        <TabsContent value="deliverables">
          <CreativeStrategistDeliverablesTab
            projectId={projectId!}
            brandId={brandId}
            onViewDeliverable={setViewingDeliverableId}
          />
        </TabsContent>

        <TabsContent value="decisions">
          <CreativeStrategistDecisionsTab projectId={projectId!} />
        </TabsContent>

        <TabsContent value="files">
          <CreativeStrategistFilesTab
            projectId={projectId!}
            onOpenMessage={(messageId) => {
              setChatOpen(true);
              setFocusMessageId(messageId);
            }}
          />
        </TabsContent>

        <TabsContent value="results">
          <CreativeStrategistResultsTab
            projectId={projectId!}
            brandId={brandId}
            onOpenDeliverables={() =>
              navigate(`${basePath}?tab=deliverables`, { replace: true })
            }
          />
        </TabsContent>

        <TabsContent value="activity">
          <Card>
            <CardContent className="p-0">
              {activityLoading ? (
                <div className="p-6 space-y-3">
                  <Skeleton className="h-5 w-2/3" />
                  <Skeleton className="h-5 w-1/2" />
                </div>
              ) : (activity ?? []).length === 0 ? (
                <div className="py-12 text-center text-sm text-muted-foreground">
                  No activity recorded yet.
                </div>
              ) : (
                <ul className="divide-y" data-testid="list-activity">
                  {(activity ?? []).map((event) => (
                    <li
                      key={event.id}
                      className="flex items-start gap-3 px-5 py-3.5"
                      data-testid={`activity-${event.id}`}
                    >
                      <CheckCircle2 className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm">{event.summary}</div>
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-0.5">
                          <CalendarClock className="h-3 w-3" />
                          {csFormatDateTime(event.createdAt)} · {event.eventType}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      </div>

      <CreativeStrategistChatPanel
        projectId={projectId!}
        viewingContext={viewingContext}
        viewingLabel={viewingLabel}
        open={chatOpen}
        onOpenChange={setChatOpen}
        focusMessageId={focusMessageId}
        onFocusHandled={() => setFocusMessageId(null)}
      />
    </div>
  );
}

// One open blocker per card, with the exact resolution options the server
// recorded. Nothing here guesses a way forward — a human picks.
function BlockerList({
  projectId,
  brandId,
  blockers,
  surface,
}: {
  projectId: string | undefined;
  brandId: string | undefined;
  blockers: CreativeStrategistBlocker[];
  surface: "overview" | "plan";
}) {
  if (blockers.length === 0) return null;

  return (
    <div className="space-y-3 mt-4" data-testid={`blockers-${surface}`}>
      {blockers.map((blocker) => (
        <BlockerCard key={blocker.id} projectId={projectId} brandId={brandId} blocker={blocker} />
      ))}
    </div>
  );
}

function BlockerCard({
  projectId,
  brandId,
  blocker,
}: {
  projectId: string | undefined;
  brandId: string | undefined;
  blocker: CreativeStrategistBlocker;
}) {
  const { toast } = useToast();
  const [picking, setPicking] = useState<string | null>(null);
  const [choice, setChoice] = useState("");
  const [typedName, setTypedName] = useState("");
  const assetType = blocker.assetType ?? "";
  const templatesKey = csTemplatesKey(brandId);

  const { data: board, isLoading: boardLoading } = useQuery<CreativeStrategistTemplateBoard>({
    queryKey: [templatesKey, assetType],
    enabled: !!brandId && !!assetType && !!picking,
    queryFn: async () => {
      const res = await fetch(`${templatesKey}?assetType=${encodeURIComponent(assetType)}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error((await res.text()) || res.statusText);
      return (await res.json()) as CreativeStrategistTemplateBoard;
    },
  });

  const coverage = board?.assetTypes?.find((a) => a.assetType === assetType);
  const needsReference = coverage?.discoverable ?? true;
  const options = (board?.entries ?? []).filter(
    (e) => e.permitted && e.status !== "RETIRED" && (e.id || e.templateId),
  );

  const resolve = useMutation({
    mutationFn: async (vars: { option: string; entryKey?: string; templateName?: string }) => {
      const entry = vars.entryKey
        ? options.find((e) => entryValue(e) === vars.entryKey)
        : undefined;
      await apiRequest("POST", `/api/creative-strategist/blockers/${blocker.id}/resolve`, {
        option: vars.option,
        ...(entry?.id ? { registryTemplateId: entry.id } : {}),
        ...(!entry?.id && entry?.templateId
          ? {
              underlyingTemplateId: entry.templateId,
              underlyingTemplateType: entry.templateType ?? undefined,
              templateName: entry.name,
            }
          : {}),
        ...(vars.templateName ? { templateName: vars.templateName } : {}),
      });
    },
    onSuccess: (_data, vars) => {
      setPicking(null);
      setChoice("");
      setTypedName("");
      if (vars.option === "propose_template") {
        toast({
          title: "Proposed",
          description: "The candidate sits in Testing until someone approves it.",
        });
      }
      queryClient.invalidateQueries({ queryKey: [csBlockersKey(projectId)] });
      queryClient.invalidateQueries({ queryKey: [templatesKey] });
      queryClient.invalidateQueries({
        queryKey: [`/api/creative-strategist/projects/${projectId}`],
      });
    },
    onError: (e: Error) =>
      toast({ title: "Couldn't resolve that", description: e.message, variant: "destructive" }),
  });

  const picksTemplate = (key: string) => key === "ask_user" || key === "propose_template";
  // Creative Strategist can pick its own candidate to propose; asking the user
  // is meaningless without their answer.
  const canConfirm =
    picking === "ask_user"
      ? !!choice
      : needsReference
        ? !!choice || options.some((e) => e.status === "UNREVIEWED")
        : !!typedName.trim();

  return (
    <Card className="border-amber-300 dark:border-amber-900" data-testid={`blocker-${blocker.id}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start gap-2.5">
          <ShieldAlert className="h-4 w-4 mt-0.5 text-amber-600 shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-medium">{blocker.title}</div>
            {blocker.detail && (
              <p className="text-sm text-muted-foreground mt-1">{blocker.detail}</p>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {(blocker.optionsJson ?? []).map((option) => (
            <Button
              key={option.key}
              size="sm"
              variant={picking === option.key ? "default" : "outline"}
              title={option.description}
              disabled={resolve.isPending}
              onClick={() =>
                picksTemplate(option.key)
                  ? (setPicking(option.key), setChoice(""), setTypedName(""))
                  : resolve.mutate({ option: option.key })
              }
              data-testid={`button-blocker-${option.key}`}
            >
              {option.label}
            </Button>
          ))}
        </div>

        {picking && (
          <div className="space-y-2 pt-1 border-t">
            {boardLoading ? (
              <Skeleton className="h-8 w-64" />
            ) : needsReference ? (
              <Select value={choice} onValueChange={setChoice}>
                <SelectTrigger className="h-8 w-full max-w-md text-sm" data-testid="select-blocker-template">
                  <SelectValue
                    placeholder={
                      picking === "ask_user"
                        ? "Pick the template to use"
                        : "Pick a template to propose (or let Creative Strategist choose)"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {options.map((entry) => (
                    <SelectItem key={entryValue(entry)} value={entryValue(entry)}>
                      {entry.name}
                      {entry.status !== "UNREVIEWED" ? ` · ${entry.status.toLowerCase()}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                autoFocus
                value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
                placeholder="Name the template to propose"
                className="h-8 w-full max-w-md text-sm"
                data-testid="input-proposed-template-name"
              />
            )}
            <div className="flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                disabled={!canConfirm || resolve.isPending}
                onClick={() =>
                  resolve.mutate({
                    option: picking,
                    entryKey: choice || undefined,
                    templateName: needsReference ? undefined : typedName.trim(),
                  })
                }
                data-testid="button-confirm-blocker-template"
              >
                {picking === "ask_user" ? "Approve and continue" : "Propose"}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setPicking(null)}
                data-testid="button-cancel-blocker-template"
              >
                Cancel
              </Button>
              <span className="text-xs text-muted-foreground">
                {picking === "ask_user"
                  ? "Approving here only endorses a template this brand may already use."
                  : "A proposal starts in Testing — approve it in the Creative System before it can be used."}
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
function OverviewStat({
  icon: Icon,
  label,
  value,
  testId,
}: {
  icon: any;
  label: string;
  value: string | number;
  testId: string;
}) {
  return (
    <div className="rounded-lg border p-3" data-testid={testId}>
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <div className="text-lg font-semibold mt-1">{value}</div>
    </div>
  );
}

/**
 * Spec §50 — Overview surfaces the resolution's blockers, not just task
 * counts. A gated tool is a project-level fact that exists before any task
 * does, and the user needs to see the concrete reason plus what they can do
 * about it rather than watching the plan quietly route around it.
 */
function OverviewBlockers({
  blockers,
  onOpenPlan,
}: {
  blockers: CreativeStrategistPlanBlocker[];
  onOpenPlan: () => void;
}) {
  return (
    <Card className="border-amber-300 dark:border-amber-800">
      <CardContent className="p-6 space-y-3" data-testid="overview-blockers">
        <div className="flex items-center justify-between gap-3">
          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
            Blockers
          </div>
          <Button size="sm" variant="ghost" onClick={onOpenPlan} data-testid="button-open-plan-blockers">
            View in plan
          </Button>
        </div>
        {blockers.map((blocker) => (
          <div key={blocker.id} data-testid={`overview-blocker-${blocker.id}`}>
            <div className="text-sm font-medium">{blocker.title}</div>
            <div className="text-xs text-muted-foreground">{blocker.reason}</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              Options: {blocker.options.map((o) => o.label).join(" · ")}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
function EmptyTab({
  icon: Icon,
  title,
  body,
  testId,
}: {
  icon: any;
  title: string;
  body: string;
  testId: string;
}) {
  return (
    <Card>
      <CardContent className="py-14 text-center space-y-2" data-testid={testId}>
        <Icon className="h-6 w-6 mx-auto text-muted-foreground" />
        <div className="text-sm font-medium">{title}</div>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">{body}</p>
      </CardContent>
    </Card>
  );
}

/** Registry rows are addressed by their own id, candidates by the underlying one. */
function entryValue(entry: CreativeStrategistTemplateEntry): string {
  return entry.id ? `row:${entry.id}` : `ref:${entry.templateId}`;
}
